import React from 'react';
import { DeviceCondition, VALID_DEVICE_TYPES, VALID_OPERATING_SYSTEMS, VALID_BROWSERS } from '../../../../types/qr.types';

interface DeviceConditionEditorProps {
  condition: DeviceCondition;
  onChange: (condition: DeviceCondition) => void;
  onRemove: () => void;
}

// Compatibility map: device type → compatible OS
const DEVICE_TO_OS: Record<string, Set<string>> = {
  mobile:  new Set(['ios', 'android']),
  desktop: new Set(['windows', 'macos', 'linux']),
  tablet:  new Set(['windows', 'macos', 'linux', 'ios', 'android']),
};

const APPLE_OS = new Set(['macos', 'ios']);

// Union of compatible OS across all selected device types. Empty input = all OS.
function compatibleOSForDevices(deviceTypes: string[]): Set<string> {
  if (deviceTypes.length === 0) return new Set(['windows','macos','linux','ios','android']);
  const union = new Set<string>();
  for (const dt of deviceTypes) {
    const osSet = DEVICE_TO_OS[dt];
    if (osSet) {
      for (const os of Array.from(osSet)) {
        union.add(os);
      }
    }
  }
  return union;
}

// Set of device types that have at least one OS in the selected list. Empty input = all devices.
function compatibleDevicesForOS(operatingSystems: string[]): Set<string> {
  if (operatingSystems.length === 0) return new Set(['mobile','desktop','tablet']);
  const selected = new Set(operatingSystems);
  const result = new Set<string>();
  for (const [device, osSet] of Object.entries(DEVICE_TO_OS)) {
    for (const os of Array.from(osSet)) {
      if (selected.has(os)) {
        result.add(device);
        break;
      }
    }
  }
  return result;
}

export function DeviceConditionEditor({ condition, onChange, onRemove }: DeviceConditionEditorProps) {
  const handleDeviceTypeToggle = (type: string) => {
    // Toggle the clicked device type
    const newDeviceTypes = condition.device_types.includes(type)
      ? condition.device_types.filter(t => t !== type)
      : [...condition.device_types, type];

    // Filter operating_systems to only those compatible with newDeviceTypes
    const compatibleOS = compatibleOSForDevices(newDeviceTypes);
    const newOS = condition.operating_systems.filter(os => compatibleOS.has(os));

    // If no Apple OS survives, drop Safari from browsers
    const hasAppleOS = newOS.some(os => APPLE_OS.has(os));
    const newBrowsers = !hasAppleOS && condition.browsers.includes('safari')
      ? condition.browsers.filter(b => b !== 'safari')
      : condition.browsers;

    onChange({
      ...condition,
      device_types: newDeviceTypes,
      operating_systems: newOS,
      browsers: newBrowsers,
    });
  };

  const handleOSToggle = (os: string) => {
    // Toggle the clicked OS
    const newOS = condition.operating_systems.includes(os)
      ? condition.operating_systems.filter(o => o !== os)
      : [...condition.operating_systems, os];

    // Filter device_types to only those compatible with newOS
    const compatibleDevices = compatibleDevicesForOS(newOS);
    const newDeviceTypes = condition.device_types.filter(dt => compatibleDevices.has(dt));

    // If no Apple OS remains, drop Safari from browsers
    const hasAppleOS = newOS.some(o => APPLE_OS.has(o));
    const newBrowsers = !hasAppleOS && condition.browsers.includes('safari')
      ? condition.browsers.filter(b => b !== 'safari')
      : condition.browsers;

    onChange({
      ...condition,
      device_types: newDeviceTypes,
      operating_systems: newOS,
      browsers: newBrowsers,
    });
  };

  const handleBrowserToggle = (browser: string) => {
    if (isBrowserDisabled(browser)) return;
    const newBrowsers = condition.browsers.includes(browser)
      ? condition.browsers.filter(b => b !== browser)
      : [...condition.browsers, browser];
    onChange({ ...condition, browsers: newBrowsers });
  };

  // Disable predicates using the compatibility map
  const isDeviceTypeDisabled = (type: string): boolean => {
    // Already selected items are never disabled
    if (condition.device_types.includes(type)) return false;
    // If no OS selected (wildcard), nothing is disabled
    if (condition.operating_systems.length === 0) return false;
    // Disabled if none of this device's compatible OS appear in current OS selection
    const compatibleOS = DEVICE_TO_OS[type] ?? new Set();
    return !condition.operating_systems.some(os => compatibleOS.has(os));
  };

  const isOSDisabled = (os: string): boolean => {
    // Already selected items are never disabled
    if (condition.operating_systems.includes(os)) return false;
    // If no devices selected (wildcard), nothing is disabled
    if (condition.device_types.length === 0) return false;
    // Disabled if not in the compatible OS for current device types
    const compatible = compatibleOSForDevices(condition.device_types);
    return !compatible.has(os);
  };

  const isBrowserDisabled = (browser: string): boolean => {
    // Already selected items are never disabled
    if (condition.browsers.includes(browser)) return false;
    // Only Safari can be disabled
    if (browser !== 'safari') return false;
    // Safari disabled when OS is non-empty and contains zero Apple OS
    if (condition.operating_systems.length === 0) return false;
    const hasAppleOS = condition.operating_systems.some(os => APPLE_OS.has(os));
    return !hasAppleOS;
  };

  const deviceIcons: Record<string, string> = {
    mobile: 'M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z',
    desktop: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    tablet: 'M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
  };

  return (
    <div className="bg-[--clinqr-bg-subtle]/50 border border-[--clinqr-border]/10 rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-[--clinqr-primary]">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </span>
          <span className="font-medium text-[--clinqr-text-primary]">Device / Browser</span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-red-500 hover:text-red-700 p-1"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="space-y-1">
        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-2">
            Device Type
            <span className="ml-2 text-xs font-normal text-[--clinqr-text-muted]">match any</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {VALID_DEVICE_TYPES.map((type) => {
              const disabled = isDeviceTypeDisabled(type);
              const selected = condition.device_types.includes(type);

              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => handleDeviceTypeToggle(type)}
                  disabled={disabled}
                  title={disabled ? `Not compatible with selected OS` : ''}
                  className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    disabled
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50'
                      : selected
                      ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border border-[--clinqr-navy]/20'
                      : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border border-[--clinqr-border]/10 hover:bg-gray-200'
                  }`}
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={deviceIcons[type]} />
                  </svg>
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 py-2">
          <div className="flex-1 h-px bg-[--clinqr-border]/10" />
          <span className="px-2 py-0.5 text-xs font-semibold text-[--clinqr-text-muted] bg-[--clinqr-bg-subtle] border border-[--clinqr-border]/10 rounded">AND</span>
          <div className="flex-1 h-px bg-[--clinqr-border]/10" />
        </div>

        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-2">
            Operating System
            <span className="ml-2 text-xs font-normal text-[--clinqr-text-muted]">match any</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {VALID_OPERATING_SYSTEMS.map((os) => {
              const disabled = isOSDisabled(os);
              const selected = condition.operating_systems.includes(os);

              return (
                <button
                  key={os}
                  type="button"
                  onClick={() => handleOSToggle(os)}
                  disabled={disabled}
                  title={disabled ? `Not compatible with selected device type` : ''}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    disabled
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50'
                      : selected
                      ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border border-[--clinqr-navy]/20'
                      : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border border-[--clinqr-border]/10 hover:bg-gray-200'
                  }`}
                >
                  {os === 'macos' ? 'macOS' : os === 'ios' ? 'iOS' : os.charAt(0).toUpperCase() + os.slice(1)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 py-2">
          <div className="flex-1 h-px bg-[--clinqr-border]/10" />
          <span className="px-2 py-0.5 text-xs font-semibold text-[--clinqr-text-muted] bg-[--clinqr-bg-subtle] border border-[--clinqr-border]/10 rounded">AND</span>
          <div className="flex-1 h-px bg-[--clinqr-border]/10" />
        </div>

        <div>
          <label className="block text-sm font-medium text-[--clinqr-text-secondary] mb-2">
            Browser
            <span className="ml-2 text-xs font-normal text-[--clinqr-text-muted]">match any</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {VALID_BROWSERS.map((browser) => {
              const disabled = isBrowserDisabled(browser);
              const selected = condition.browsers.includes(browser);

              return (
                <button
                  key={browser}
                  type="button"
                  onClick={() => handleBrowserToggle(browser)}
                  disabled={disabled}
                  title={disabled ? `${browser.charAt(0).toUpperCase() + browser.slice(1)} is only available on Apple devices` : ''}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    disabled
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50'
                      : selected
                      ? 'bg-[--clinqr-lime] text-[--clinqr-navy] border border-[--clinqr-navy]/20'
                      : 'bg-[--clinqr-bg-subtle] text-[--clinqr-text-secondary] border border-[--clinqr-border]/10 hover:bg-gray-200'
                  }`}
                >
                  {browser.charAt(0).toUpperCase() + browser.slice(1)}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
